<template>
	<div class="note-rich-text">
		<div
			ref="content"
			class="note-rich-text__body"
			:class="{
				'note-rich-text__body--collapsed': isCollapsed && canCollapse
			}"
			:style="bodyStyle"
			v-html="safeHtml"
		/>

		<button
			v-if="canCollapse"
			type="button"
			class="note-rich-text__toggle"
			@click="toggle"
		>
			<span>{{ isCollapsed ? moreLabel : lessLabel }}</span>
			<svg
				v-if="!isCollapsed"
				class="note-rich-text__toggle-icon"
				width="12"
				height="12"
				viewBox="0 0 12 12"
				fill="none"
				aria-hidden="true"
			>
				<path
					d="M9.75 7.5L6 3.75L2.25 7.5"
					stroke="currentColor"
					stroke-width="1.25"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			</svg>
		</button>
	</div>
</template>

<script>
import { sanitizeRichTextHtml } from '~/utils/richTextSanitizer'

export default {
	name: 'CollapsibleRichTextNote',
	props: {
		content: {
			type: String,
			default: ''
		},
		collapsedHeight: {
			type: Number,
			default: 60
		},
		moreLabel: {
			type: String,
			default: ''
		},
		lessLabel: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			isCollapsed: true,
			canCollapse: false
		}
	},
	computed: {
		bodyStyle() {
			if (this.isCollapsed && this.canCollapse) {
				return { maxHeight: `${this.collapsedHeight}px` }
			}

			return null
		},
		safeHtml() {
			return sanitizeRichTextHtml(this.content)
		}
	},
	watch: {
		safeHtml() {
			this.isCollapsed = true
			this.$nextTick(this.measureOverflow)
		}
	},
	mounted() {
		this.measureOverflow()
	},
	methods: {
		toggle() {
			this.isCollapsed = !this.isCollapsed
		},
		measureOverflow() {
			const el = this.$refs.content
			if (!el) {
				this.canCollapse = false
				return
			}

			this.canCollapse = el.scrollHeight > this.collapsedHeight + 1
		}
	}
}
</script>

<style scoped>
.note-rich-text__body {
	font-size: 14px;
	line-height: 20px;
	color: #4d4d4c;
	word-break: break-word;
}

.note-rich-text__body--collapsed {
	overflow: hidden;
}

.note-rich-text__toggle {
	padding: 8px 0 0;
	font-size: 14px;
	line-height: normal;
	font-weight: 500;
	font-family: Roboto, sans-serif;
	color: #315f73;
	background: transparent;
	border: 0;
	white-space: nowrap;
	display: inline-flex;
	align-items: center;
	gap: 8px;
}

.note-rich-text__toggle-icon {
	flex-shrink: 0;
}

.note-rich-text__toggle:focus-visible {
	outline: 2px solid #b8cde4;
	outline-offset: 2px;
	border-radius: 4px;
}

.note-rich-text__body--collapsed::after {
	content: '';
	display: block;
	height: 0;
}

.note-rich-text__body ::v-deep p {
	margin: 0;
}

.note-rich-text__body ::v-deep ul,
.note-rich-text__body ::v-deep ol {
	margin: 0;
	padding-left: 18px;
}

.note-rich-text__body ::v-deep ul {
	list-style-type: disc;
}

.note-rich-text__body ::v-deep ol {
	list-style-type: decimal;
}

.note-rich-text__body ::v-deep li {
	margin: 0;
}
</style>
